// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyInstallment, TyInstallmentPlan } from "@/types";
import { formatMoney } from "@/utils";
import InstallmentCard from "@/UI/cards/InstallmentCard";
import PlanCard from "@/UI/cards/PlanCard";
import ActionsBar from "@/UI/components/ActionsBar";
import CrmStats from "@/UI/components/CrmStats";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import ScopeTabs from "@/UI/components/ScopeTabs";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import SelectInput from "@/UI/inputs/SelectInput";
import PaymentFormPopup from "@/UI/popups/PaymentFormPopup";
import PlanTimelinePopup from "@/UI/popups/PlanTimelinePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyResponse = {
    installments: TyInstallment[];
    total: number;
    totals: { overdue: number; overdueCount: number; upcoming: number; upcomingCount: number };
};

/**
 * The money, and when it is owed.
 *
 * The CRM's third tab, because an installment is a fact about a customer rather than about a unit:
 * the question it answers is "who owes us something this month", and the answer is a list of
 * people. Due order, soonest first, which is the only order this list is ever read in.
 *
 * **Two figures above it, and they are the point.** What is already late and what is coming — over
 * the whole company rather than the page, because a number that changes when you turn the page is
 * not a number anybody can report upwards.
 *
 * **Two views, because they are two questions.** *Upcoming* is the default and lists the rows
 * themselves, soonest first — what is owed, by whom, this month. *All* lists the **plans**: how
 * each agreement is going, as a bar. A list of rows cannot answer the second, and a list of plans
 * cannot answer the first, so neither one is a filter of the other.
 *
 * Paying lands on the **plan**, never on the row: one payment often settles two and a half of
 * these, and the allocator spreads it. See `PaymentFormPopup`.
 */
export default function InstallmentsScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    /** `upcoming` is the rows, soonest first; `all` is the plans behind them. */
    const view = params.get("view") === "all" ? "all" : "upcoming";
    const status = params.get("status") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftStatus, setDraftStatus] = useState(status);

    const rows = useFetch<TyResponse>("/sales/installments", {}, false);
    const plans = useFetch<{ plans: TyInstallmentPlan[] }>("/sales/plans", {}, false);

    const load = () => {
        /**
         * The totals ride on the rows, so they are fetched for both views and only listed for one.
         * `next` keeps one row per plan — the soonest still owed — because a twelve-row plan
         * otherwise fills this list with its own schedule and hides every other customer.
         */
        const query: Record<string, unknown> = { limit: pageSize, page, due: "open", next: "1" };
        if (status) query.status = status;
        rows.invoke({ query });
        if (view === "all") plans.invoke({ query: { limit: constants.maxPageSize } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, status, page, pageSize]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const openPayment = (planId: string, suggested: number | null) =>
        addComp({ name: "payment-form", Component: PaymentFormPopup, anim: "scale", details: { planId, suggested, onDone: load } });

    const openSchedule = (plan: TyInstallmentPlan) =>
        addComp({ name: "plan-timeline", Component: PlanTimelinePopup, place: "end", anim: "endSlide", details: { plan } });

    const payRow = (installment: TyInstallment) => {
        const planId = typeof installment.plan === "object" && installment.plan ? installment.plan._id : String(installment.plan);
        openPayment(planId, Math.max((installment.amount ?? 0) - (installment.paidAmount ?? 0), 0));
    };

    const totals = rows.data?.totals;

    return (
        <section className="installments-screen">
            <CrmStats />

            <ActionsBar
                tabs={[
                    { key: "customers", label: t.crm.customers },
                    { key: "leads", label: t.crm.interests },
                    { key: "installments", label: t.sales.installments },
                ]}
                activeTab="installments"
                onTab={(key) => key !== "installments" && navigate(key === "customers" ? "/crm" : "/crm/leads")}
                isFilterOpen={isFiltering}
                onFilterOpen={() => {
                    setDraftStatus(status);
                    setIsFiltering(true);
                }}
                hasFilters={!!status}
                onFilterApply={() => {
                    setIsFiltering(false);
                    apply({ status: draftStatus || null });
                }}
                onFilterClear={() => {
                    setDraftStatus("");
                    setIsFiltering(false);
                    apply({ status: null });
                }}
                onFilterCancel={() => setIsFiltering(false)}
                filterContent={
                    <>
                        <FilterSearchInput value="" onChange={() => {}} label={t.common.search} placeholder={t.crm.searchCustomers} />
                        <SelectInput
                            compact
                            label={t.crm.fields.status}
                            value={draftStatus}
                            onChange={setDraftStatus}
                            options={[
                                { value: "", label: t.crm.all },
                                { value: "pending", label: t.sales.statuses.pending },
                                { value: "paid", label: t.sales.statuses.paid },
                            ]}
                        />
                    </>
                }
            />

            {/* What is late and what is coming, over everything rather than the page. */}
            {totals && (
                <div className="installments-screen__totals">
                    <article className="installments-screen__total installments-screen__total--overdue">
                        <span>{t.sales.overdue}</span>
                        <strong>
                            <bdi dir="ltr">{formatMoney(totals.overdue)}</bdi>
                        </strong>
                        <small>{totals.overdueCount}</small>
                    </article>
                    <article className="installments-screen__total">
                        <span>{t.sales.upcoming}</span>
                        <strong>
                            <bdi dir="ltr">{formatMoney(totals.upcoming)}</bdi>
                        </strong>
                        <small>{totals.upcomingCount}</small>
                    </article>
                </div>
            )}

            {/* The rows, or the plans behind them. Two questions, not one filtered twice. */}
            <ScopeTabs
                value={view === "all" ? "mine" : "all"}
                onChange={(scope) => apply({ view: scope === "mine" ? "all" : null, page: null })}
                allLabel={t.sales.upcoming}
                mineLabel={t.sales.plans}
            />

            {view === "upcoming" ? (
                <>
                    {rows.loading && <p className="installments-screen__state">{t.common.loading}</p>}
                    {rows.error && <p className="installments-screen__state installments-screen__state--error">{rows.error}</p>}
                    {!rows.loading && !rows.error && !rows.data?.installments.length && (
                        <EmptyState icon="subscription" title={t.sales.emptyInstallments} description={t.sales.emptyInstallmentsHint} />
                    )}

                    <div className="installments-screen__grid">
                        {(rows.data?.installments ?? []).map((installment) => (
                            <InstallmentCard key={installment._id} installment={installment} onPay={() => payRow(installment)} />
                        ))}
                    </div>
                </>
            ) : (
                <>
                    {plans.loading && <p className="installments-screen__state">{t.common.loading}</p>}
                    {plans.error && <p className="installments-screen__state installments-screen__state--error">{plans.error}</p>}
                    {!plans.loading && !plans.error && !plans.data?.plans.length && (
                        <EmptyState icon="stat" title={t.sales.emptyPlans} description={t.sales.emptyPlansHint} />
                    )}

                    <div className="installments-screen__grid">
                        {(plans.data?.plans ?? []).map((plan) => (
                            <PlanCard
                                key={plan._id}
                                plan={plan}
                                onSchedule={() => openSchedule(plan)}
                                onPay={() => openPayment(plan._id, Math.max((plan.totalAmount ?? 0) - (plan.paidAmount ?? 0), 0))}
                            />
                        ))}
                    </div>
                </>
            )}

            {view === "upcoming" && (
                <Paginator
                    page={page}
                    total={rows.data?.total ?? 0}
                    count={rows.data?.installments.length ?? 0}
                    onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
                />
            )}
        </section>
    );
}
